import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { useAppStore } from "@/store/useAppStore";
import {
  LayoutDashboard,
  Inbox,
  Send,
  Activity,
  FileText,
  Users,
  BookOpen,
  BarChart3,
  Settings,
} from "lucide-react";
import { CONTENT_ENABLED } from "@/lib/featureFlags";
import { conversationsApi } from "@/lib/api";
import { channelLabel } from "@/components/common/ChannelIcon";

function conversationTitle(c) {
  return c.contact_name?.trim() || c.external_thread_id?.trim() || "Unknown sender";
}

function conversationSearchValue(c) {
  return [
    conversationTitle(c),
    c.contact_name,
    c.external_thread_id,
    c.last_message_preview,
    c.channel,
    channelLabel(c.channel),
  ].filter(Boolean).join(" ");
}

/**
 * Strict search filter that matches search terms against word boundaries or prefixes.
 * A search term must match the start of a word in the search value.
 * For example:
 * - "sig" matches "signals" (prefix match)
 * - "sils" does NOT match "signals" (not a word boundary match)
 * - "signal report" matches "Reports · Lead Reports" (both terms must match at word starts)
 */
function matchesSearch(searchValue, searchQuery) {
  if (!searchQuery || !searchValue) return true;
  
  // Normalize both strings: lowercase and collapse multiple spaces
  const normalized = (str) => String(str || "").toLowerCase().replace(/\s+/g, " ").trim();
  const value = normalized(searchValue);
  const query = normalized(searchQuery);
  
  // If query is empty after normalization, match everything
  if (!query) return true;
  
  // Split query into individual terms
  const terms = query.split(" ").filter(Boolean);
  
  // Split value into words (split on non-alphanumeric characters to capture word boundaries)
  const words = value.split(/[\W_]+/).filter(Boolean);
  
  // Each term must match the start of at least one word in the value
  return terms.every((term) => {
    return words.some((word) => word.startsWith(term));
  });
}

const navTargets = [
  { label: "Dashboard", to: "/dashboard", icon: LayoutDashboard, group: "Navigate" },
  { label: "Today", to: "/today", icon: LayoutDashboard, group: "Navigate" },
  { label: "Inbox", to: "/inbox", icon: Inbox, group: "Navigate" },
  { label: "Outreach", to: "/outreach", icon: Send, group: "Modules" },
  { label: "Signals", to: "/signals", icon: Activity, group: "Modules" },
  { label: "Reports · Lead Reports", to: "/reports/leads", icon: BarChart3, group: "Modules" },
  { label: "Content", to: "/content", icon: FileText, group: "Modules", extendedOnly: true },
  { label: "CRM", to: "/crm", icon: Users, group: "Modules" },
  { label: "Brain", to: "/brain", icon: BookOpen, group: "Modules" },
  { label: "Analytics", to: "/analytics", icon: BarChart3, group: "Modules" },
  { label: "Settings · Connections", to: "/settings/connections", icon: Settings, group: "Modules" },
];

/** Temporary — remove paths here to show them again in the palette. */
const HIDDEN_PALETTE_PATHS = new Set([
  "/dashboard",
  "/crm",
  "/analytics",
  "/settings/connections",
]);

export function CommandPalette() {
  const { paletteOpen, setPaletteOpen, auth } = useAppStore();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [conversations, setConversations] = useState([]);
  const isDemo = auth?.mode === "demo";

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setPaletteOpen]);

  useEffect(() => {
    if (!paletteOpen) {
      setSearch("");
      setDebouncedSearch("");
    }
  }, [paletteOpen]);

  useEffect(() => {
    // Preserve internal whitespace but trim edges and normalize multiple spaces to single space
    const normalized = search.trim().replace(/\s+/g, ' ');
    const timer = setTimeout(() => setDebouncedSearch(normalized), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!paletteOpen || isDemo) {
      setConversations([]);
      return;
    }
    let cancelled = false;
    conversationsApi.list({
      limit: debouncedSearch ? 20 : 8,
      q: debouncedSearch || undefined,
    })
      .then((r) => { if (!cancelled) setConversations(r.conversations || []); })
      .catch(() => { if (!cancelled) setConversations([]); });
    return () => { cancelled = true; };
  }, [paletteOpen, isDemo, debouncedSearch]);

  const go = (to) => {
    setPaletteOpen(false);
    navigate(to);
  };

  return (
    <Dialog open={paletteOpen} onOpenChange={setPaletteOpen}>
      <DialogContent className="p-0 max-w-xl overflow-hidden" data-testid="command-palette">
        <VisuallyHidden.Root>
          <DialogTitle>Command palette</DialogTitle>
          <DialogDescription>Search pages, channels, conversations and modules.</DialogDescription>
        </VisuallyHidden.Root>
        <Command className="bg-white" shouldFilter={false}>
          <CommandInput
            placeholder="Type a command, search a page or contact…"
            value={search}
            onValueChange={setSearch}
            data-testid="command-input"
          />
          <CommandList className="max-h-[420px]">
            <CommandEmpty>No results.</CommandEmpty>
            {["Navigate", "Modules"].map((group) => {
              const filteredTargets = navTargets.filter((t) => 
                t.group === group 
                && !HIDDEN_PALETTE_PATHS.has(t.to) 
                && (CONTENT_ENABLED || !t.extendedOnly)
                && matchesSearch(t.label, debouncedSearch)
              );
              return filteredTargets.length > 0 ? (
                <CommandGroup key={group} heading={group}>
                  {filteredTargets.map((t) => (
                    <CommandItem key={t.to} value={t.label} onSelect={() => go(t.to)} className="gap-2">
                      <t.icon size={14} strokeWidth={1.75} className="text-slate-500" />
                      <span>{t.label}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : null;
            })}
            {!isDemo && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Conversations">
                  {conversations.length === 0 ? (
                    <CommandItem disabled value="no-conversations" className="text-slate-500">
                      No conversations in inbox
                    </CommandItem>
                  ) : conversations
                    .filter((c) => matchesSearch(conversationSearchValue(c), debouncedSearch))
                    .map((c) => (
                    <CommandItem
                      key={c.id}
                      value={conversationSearchValue(c)}
                      onSelect={() => go(`/inbox/${c.id}`)}
                      className="gap-2"
                    >
                      <span className="h-2 w-2 rounded-full bg-indigo-500 shrink-0" />
                      <span className="truncate">{conversationTitle(c)}</span>
                      <span className="ml-auto text-[11px] text-slate-400 truncate max-w-[45%]">
                        {c.last_message_preview || channelLabel(c.channel)}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
